if (!customElements.get('products-comparison')) {
  customElements.define(
    'products-comparison',
    class ProductsComparison extends HTMLElement {
      connectedCallback() {
        this.slider = this.querySelector('[data-products-comparison-slider]');
        this.labelsSlide = this.querySelector('[data-products-comparison-labels-slide]');
        this.highlightedSlide = this.querySelector('[data-products-comparison-highlighted]');
        this.slides = Array.from(this.querySelectorAll('[data-products-comparison-slide]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-products-comparison-thumb]'));
        this.rowGroups = ['title', 'row-1', 'row-2', 'row-3'];

        if (!this.slider) {
          return;
        }

        this.handleResize = this.handleResize.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.handleThumbActivate = this.handleThumbActivate.bind(this);
        this.handleThumbKeydown = this.handleThumbKeydown.bind(this);

        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', this.handleThumbActivate);
          thumb.addEventListener('keydown', this.handleThumbKeydown);
        });

        this.slider.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('resize', this.handleResize);

        this.syncLayout();
      }

      disconnectedCallback() {
        this.thumbs.forEach((thumb) => {
          thumb.removeEventListener('click', this.handleThumbActivate);
          thumb.removeEventListener('keydown', this.handleThumbKeydown);
        });

        if (this.slider) {
          this.slider.removeEventListener('scroll', this.handleScroll);
        }

        window.removeEventListener('resize', this.handleResize);
      }

      handleResize() {
        this.syncLayout();
      }

      handleScroll() {
        window.clearTimeout(this.scrollTimeout);
        this.scrollTimeout = window.setTimeout(() => {
          this.syncThumbState();
        }, 40);
      }

      handleThumbKeydown(event) {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }

        event.preventDefault();
        this.handleThumbActivate(event);
      }

      handleThumbActivate(event) {
        if (!this.slider) {
          return;
        }

        const thumb = event.currentTarget;
        const slideIndex = Number(thumb.dataset.productsComparisonSlideIndex);

        if (Number.isNaN(slideIndex)) {
          return;
        }

        this.collectSnapPoints();

        this.slider.scrollTo({
          left: this.snapPoints[slideIndex] || 0,
          behavior: 'smooth'
        });

        this.setActiveThumb(slideIndex);
      }

      syncLayout() {
        this.syncRowHeights();
        this.collectSnapPoints();
        this.syncThumbState();
      }

      syncRowHeights() {
        this.rowGroups.forEach((groupName) => {
          const rows = Array.from(
            this.querySelectorAll(`[data-products-comparison-row-group="${groupName}"]`)
          );

          rows.forEach((row) => {
            row.style.minHeight = '';
          });

          let maxHeight = 0;

          rows.forEach((row) => {
            maxHeight = Math.max(maxHeight, row.offsetHeight);
          });

          rows.forEach((row) => {
            row.style.minHeight = `${maxHeight}px`;
          });
        });
      }

      collectSnapPoints() {
        if (!this.slider || this.slides.length === 0) {
          this.snapPoints = [];
          return;
        }

        const maxScroll = Math.max(this.slider.scrollWidth - this.slider.clientWidth, 0);

        this.snapPoints = this.slides.map((slide, index) => {
          if (index === 0) {
            return 0;
          }

          const targetPoint = slide.offsetLeft - slide.offsetWidth;
          return Math.max(0, Math.min(targetPoint, maxScroll));
        });
      }

      syncThumbState() {
        if (!window.matchMedia('(max-width: 749px)').matches || this.thumbs.length === 0) {
          return;
        }

        if (!this.snapPoints || this.snapPoints.length === 0) {
          this.collectSnapPoints();
        }

        const currentScroll = this.slider.scrollLeft;
        let activeIndex = 0;
        let smallestDistance = Number.POSITIVE_INFINITY;

        this.snapPoints.forEach((point, index) => {
          const distance = Math.abs(point - currentScroll);

          if (distance < smallestDistance) {
            smallestDistance = distance;
            activeIndex = index;
          }
        });

        this.setActiveThumb(activeIndex);
      }

      setActiveThumb(activeIndex) {
        this.thumbs.forEach((thumb, index) => {
          const isActive = index === activeIndex;
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      }
    }
  );
}
